import { useState } from "react";
import { Check, ChevronUp, Award, ScrollText } from "lucide-react";
import { usePal, PalHead, fmtKM, FOCUS, PalIcon } from "./PalUI";
import PriceGate from "../../shared/PriceGate";
import { Switch } from "../../../ui/switch";
import { usePriceVisibility } from "../../context/PriceVisibilityContext";

const minus = (a, b) => Math.round((a - b) * 100) / 100;

const OptionTable = ({ o, adOn, adMg, netLabel }) => {
  const { ink, muted, dim, hair, brand, surface } = usePal();
  const num = "py-1.5 text-right tabular-nums";
  return (
    <div className={`rounded-2xl border p-5 ${surface}`} data-testid={`pal-option-${o.id}`}>
      <div className={`text-[20px] font-semibold tracking-tight ${ink}`}>{o.label}</div>
      <div className={`text-[13px] ${muted}`}>{o.name}</div>
      <table className="w-full mt-3 text-[14px]" data-testid={`pal-price-table-${o.id}`}>
        <caption className="sr-only">{o.label}, USD</caption>
        <thead>
          <tr className={`border-b ${hair}`}>
            <th scope="col" className={`text-left py-2 text-[11px] uppercase tracking-wider ${dim}`}>USD</th>
            <th scope="col" className={`text-right py-2 text-[11px] uppercase tracking-wider ${dim}`}>Quarterly</th>
            <th scope="col" className={`text-right py-2 text-[11px] uppercase tracking-wider ${dim}`}>Annual</th>
          </tr>
        </thead>
        <tbody>
          {o.lines.map((l) => (
            <tr key={l.category} className={`border-b ${hair}`}>
              <th scope="row" className={`text-left py-1.5 font-medium ${ink}`}>
                <span className="inline-flex items-center gap-2"><PalIcon name={l.iconName} className={`w-4 h-4 ${dim}`} />{l.category}</span>
              </th>
              <td className={`${num} ${ink}`} data-testid={`price-${o.id}-${l.category.toLowerCase()}-q`}>{fmtKM(l.quarterly)}</td>
              <td className={`${num} ${ink}`} data-testid={`price-${o.id}-${l.category.toLowerCase()}-a`}>{fmtKM(l.annual)}</td>
            </tr>
          ))}
          <tr className={adOn ? `border-b ${hair}` : ""}>
            <th scope="row" className="text-left py-2 font-semibold" style={{ color: brand }}>Total</th>
            <td className={`${num} font-semibold ${ink}`} data-testid={`price-${o.id}-total-q`}>{fmtKM(o.total.quarterly)}</td>
            <td className={`${num} font-semibold ${ink}`} data-testid={`price-${o.id}-total-a`}>{fmtKM(o.total.annual)}</td>
          </tr>
          {adOn && (
            <>
              <tr className={`border-b ${hair}`}>
                <th scope="row" className="text-left py-1.5 font-medium text-emerald-500">{adMg.label}</th>
                <td className={`${num} text-emerald-500`}>−{fmtKM(adMg.quarterly)}</td>
                <td className={`${num} text-emerald-500`}>−{fmtKM(adMg.annual)}</td>
              </tr>
              <tr>
                <th scope="row" className={`text-left py-2 font-semibold ${ink}`}>{netLabel}</th>
                <td className={`${num} font-semibold ${ink}`} data-testid={`price-${o.id}-net-q`}>{fmtKM(minus(o.total.quarterly, adMg.quarterly))}</td>
                <td className={`${num} font-semibold ${ink}`} data-testid={`price-${o.id}-net-a`}>{fmtKM(minus(o.total.annual, adMg.annual))}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

const Details = ({ k }) => {
  const { ink, muted, dim, brand, surface } = usePal();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="pal-details">
      <div className={`rounded-2xl border p-5 ${surface}`} data-testid="pal-strengths">
        <h3 className={`text-[17px] font-semibold ${ink}`}>{k.strengthsTitle}</h3>
        <ul className="mt-3 space-y-2">
          {k.strengths.map((s) => (
            <li key={s} className={`flex gap-2 text-[14px] ${muted}`}>
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: brand }} aria-hidden="true" />{s}
            </li>
          ))}
        </ul>
      </div>
      <div className={`rounded-2xl border p-5 ${surface}`} data-testid="pal-terms">
        <h3 className={`text-[17px] font-semibold ${ink}`}>{k.termsTitle}</h3>
        <dl className="mt-3 space-y-2.5 text-[14px]">
          {k.terms.map((t) => (
            <div key={t.label} className="grid grid-cols-1 sm:grid-cols-[170px_1fr] gap-1 sm:gap-3">
              <dt className={`font-semibold ${ink}`}>{t.label}</dt>
              <dd className={muted}>{t.value}</dd>
            </div>
          ))}
        </dl>
        <p className={`mt-3 text-[12px] ${dim}`}>{k.termsNote}</p>
      </div>
    </div>
  );
};

const PalCommercials = () => {
  const { c, isDark, brand, ink, hair } = usePal();
  const k = c.commercials;
  const { revealed, toggle } = usePriceVisibility();
  const [adOn, setAdOn] = useState(false);
  const [details, setDetails] = useState(false);
  const itemBtn = `inline-flex items-center gap-2 h-10 px-4 rounded-full border text-[13px] font-semibold ${hair} ${ink} ${FOCUS}`;
  return (
    <PriceGate accentColor={brand} isDark={isDark} revealed={revealed} onToggle={toggle}>
      {({ revealed: shown, Toggle, lockedNode }) => (
        <section aria-label={k.eyebrow} data-testid="pal-commercials">
          <PalHead eyebrow={k.eyebrow} title={k.title} accentLine={k.accentLine} lead={k.lead} action={Toggle} />
          <div className={`mt-6 pt-5 border-t flex flex-wrap items-center gap-3 ${hair}`}>
            <button type="button" className={itemBtn} aria-expanded={details} onClick={() => setDetails((v) => !v)} data-testid="pal-toggle-strengths">
              <Award className="w-4 h-4" style={{ color: brand }} aria-hidden="true" /> {k.strengthsTitle}
            </button>
            <button type="button" className={itemBtn} aria-expanded={details} onClick={() => setDetails((v) => !v)} data-testid="pal-toggle-terms">
              <ScrollText className="w-4 h-4" style={{ color: brand }} aria-hidden="true" /> {k.termsTitle}
            </button>
            {details && (
              <button type="button" className={itemBtn} onClick={() => setDetails(false)} data-testid="pal-back-to-prices">
                <ChevronUp className="w-4 h-4" aria-hidden="true" /> Back to prices
              </button>
            )}
            {shown && !details && (
              <label className={`ml-auto inline-flex items-center gap-3 text-[13px] font-semibold cursor-pointer ${ink}`}>
                <Switch checked={adOn} onCheckedChange={setAdOn} data-testid="pal-ad-mg-toggle" aria-label={k.adToggleLabel} />
                {k.adToggleLabel}
              </label>
            )}
          </div>
          <div className="mt-6">
            {details ? (
              <Details k={k} />
            ) : shown ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {k.options.map((o) => <OptionTable key={o.id} o={o} adOn={adOn} adMg={k.adMg} netLabel={k.netLabel} />)}
              </div>
            ) : (
              lockedNode
            )}
          </div>
        </section>
      )}
    </PriceGate>
  );
};

export default PalCommercials;
